/**
 * Comparison page content. One entry per competitor, one static route per entry
 * (prerenderer skips param routes, so each page is a thin wrapper component).
 *
 * Claims discipline: every factual claim about a competitor here is either
 * publicly documented by that competitor or sourced from
 * `projects/brands/fugue-lab/docs/strategy/business-model.md` competitive
 * research. Our own claims describe live capabilities only. Pricing for our
 * side lives on /packages (single source), so these pages link there instead
 * of hard-coding numbers.
 */

export interface CompareRow {
  dimension: string
  fugueLab: string
  them: string
}

export interface CompareFaq {
  question: string
  answer: string
}

export interface Competitor {
  slug: string
  /** Short name used in nav + index cards, e.g. "Acceptd" */
  name: string
  seoTitle: string
  seoDescription: string
  heroHeadline: string
  heroBody: string
  /** One honest paragraph: the short version of the whole page. */
  shortVersion: string
  rows: CompareRow[]
  /** Where the competitor genuinely fits. Honest, factual, no hedging. */
  whereTheyFit: { heading: string; body: string }
  switchSteps: { title: string; description: string }[]
  faqs: CompareFaq[]
  ctaHeading: string
  ctaBody: string
}

export const competitors: Competitor[] = [
  {
    slug: 'acceptd',
    name: 'Acceptd',
    seoTitle: 'Acceptd Alternative for Music Festivals',
    seoDescription:
      'Compare Fugue Lab and Acceptd for music festival applications. Flat monthly pricing with no per-applicant fees, music-native application fields, and your website and applications on one system.',
    heroHeadline: 'The Acceptd alternative for festivals that want their website and applications on one system',
    heroBody:
      'Acceptd handles application intake. Your website, your season content, and your applicant communications still live somewhere else. Fugue Lab runs all of it together, for a flat monthly rate with no per-applicant fees.',
    shortVersion:
      'Acceptd is the incumbent for conservatory and festival admissions, and it charges accordingly: a monthly base plus a fee for every applicant who applies. It was acquired by Togetherwork, a private equity rollup, in 2021. Fugue Lab was built by a conductor who ran festival admissions himself. You get music-native application forms, review workflow, and the festival website itself, maintained for you, at a flat monthly rate. Your applicant count stops being a line item.',
    rows: [
      {
        dimension: 'Pricing model',
        fugueLab: 'Flat monthly rate. No per-applicant fees. See packages for tiers.',
        them: 'Monthly base plus per-applicant fees, with pricing gated behind a sales call.',
      },
      {
        dimension: 'Your website',
        fugueLab: 'Included. Applications live on your own site, in your own design.',
        them: 'Not included. Applicants leave your site to apply on getacceptd.com.',
      },
      {
        dimension: 'Music-native fields',
        fugueLab:
          'Repertoire lists, per-movement audition video, teacher recommendations, instrument and voice-part routing.',
        them: 'Media upload and forms built for arts admissions generally.',
      },
      {
        dimension: 'Who builds and runs it',
        fugueLab: 'A working conductor and software engineer, directly. You talk to the person who builds it.',
        them: 'Support queue at a private-equity-owned platform (Togetherwork, 2021).',
      },
      {
        dimension: 'Applicant communications',
        fugueLab: 'Email sequences for applicants and admits, on the same system as the forms.',
        them: 'In-platform messaging; your season marketing still lives elsewhere.',
      },
      {
        dimension: 'Contract shape',
        fugueLab: 'Month to month. Your content and applicant data export cleanly if you leave.',
        them: 'Annual agreements are standard.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Acceptd fits',
      body: 'Acceptd runs admissions for many of the largest conservatories and S-tier festivals, and its applicant marketplace puts your program in front of students already browsing there. If marketplace discovery is the main thing you want, that network is real. If what you want is your own applications, on your own site, without paying per applicant, that is what we build.',
    },
    switchSteps: [
      {
        title: 'We map your current application',
        description:
          'Every field, fee, deadline, and reviewer step from your existing Acceptd setup, documented before anything moves.',
      },
      {
        title: 'We rebuild it on your site',
        description: 'Music-native forms in your own design, tested with your team before applications open.',
      },
      {
        title: 'You open the season',
        description:
          'Applicants apply on your domain. Your team reviews in one place. We stay on for support through the cycle.',
      },
    ],
    faqs: [
      {
        question: 'Can you handle audition video uploads?',
        answer:
          'Yes. Applicants upload video per piece or per movement, and reviewers watch inside the review workflow. Reliable large-file upload was a day-one requirement, not an add-on.',
      },
      {
        question: 'What happens to our historical applicant data?',
        answer:
          'Export it from Acceptd and we import it, so prior-year applicants and their materials stay searchable alongside new seasons.',
      },
      {
        question: 'Our applications open in a few months. Is that enough time?',
        answer:
          'A typical application rebuild takes weeks, not months. Tell us your open date on the call and you get a straight answer about whether it is realistic.',
      },
    ],
    ctaHeading: 'Run applications on your own site',
    ctaBody:
      'Book a call and walk through your current Acceptd setup. You leave with a concrete picture of what the same season looks like on Fugue Lab, and what it costs.',
  },
  {
    slug: 'squarespace',
    name: 'Squarespace',
    seoTitle: 'Squarespace Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and Squarespace for music festivals, ensembles, schools, and churches. Original design, music-native structure, and a site someone runs for you instead of a template you maintain yourself.',
    heroHeadline: 'What Squarespace templates cannot do for a music organization',
    heroBody:
      'Squarespace is fine for a portfolio. A festival with applications, a season calendar, faculty bios, and four programs is not a portfolio. At some point you stop designing your site and start fighting the template.',
    shortVersion:
      'Squarespace gives you a template and an editor, and everything past that is your job: the design decisions, the updates, the workarounds when the template will not do what your season needs. Fugue Lab designs your site around how music organizations actually work, with programs, rosters, events, and application funnels as first-class structure, then keeps running it with you. You edit content through a CMS that cannot break the design.',
    rows: [
      {
        dimension: 'Design',
        fugueLab: 'Designed from scratch around your organization and season. No template underneath.',
        them: 'Template you customize within its limits. Sites in the same template family look related.',
      },
      {
        dimension: 'Music-org structure',
        fugueLab: 'Programs, faculty, repertoire, events, and applications modeled directly.',
        them: 'Generic pages and collections you adapt yourself.',
      },
      {
        dimension: 'Applications and auditions',
        fugueLab: 'Application funnels with payments and review workflow, on your own domain.',
        them: 'Third-party form embeds. Applicants leave your design, and the data lives in another tool.',
      },
      {
        dimension: 'Who does the work',
        fugueLab: 'We design, build, and maintain it. You edit content when you want to.',
        them: 'You, or a freelancer you find and manage.',
      },
      {
        dimension: 'Performance and SEO',
        fugueLab: 'Prerendered static pages. Every route ships real HTML that search engines index.',
        them: 'Depends on the template and what has been embedded into it.',
      },
      {
        dimension: 'Content editing',
        fugueLab: 'A CMS scoped to your content. You can change the words and images, not break the layout.',
        them: 'Full drag-and-drop editor, which is also how layouts get accidentally broken.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Squarespace fits',
      body: 'A soloist who needs a bio, photos, and a contact form can be live on Squarespace this weekend for very little money, and that is a reasonable call. The math changes when there is an organization behind the site: seasons, rosters, applications, and a volunteer or admin who inherits the maintenance. That is the point where a template stops being cheap.',
    },
    switchSteps: [
      {
        title: 'We audit what you have',
        description:
          'Content, pages, traffic, and what your current site is silently costing you in speed, search ranking, and workarounds.',
      },
      {
        title: 'We design and build the real thing',
        description: 'A site designed around your programs and season, reviewed with your team before launch.',
      },
      {
        title: 'Launch without losing your history',
        description:
          'Your domain moves cleanly, old URLs redirect, and search engines keep everything you have earned.',
      },
    ],
    faqs: [
      {
        question: 'We already paid for a year of Squarespace. Does switching make sense mid-cycle?',
        answer:
          'Usually the decision is about the season, not the subscription. If applications or a season launch is coming, the remaining subscription cost is small next to what a site that converts is worth. If nothing is coming up, finish the year and plan the rebuild for the off-season.',
      },
      {
        question: 'Can we still edit our own content?',
        answer:
          'Yes. You get a CMS for the content that changes: events, rosters, news, programs. The design itself stays fixed, which is the point.',
      },
      {
        question: 'What does this cost compared to Squarespace?',
        answer:
          'More than a template subscription, because it includes the design, the build, and a person maintaining it. Packages are on the pricing page, and the call gives you an exact number for your situation.',
      },
    ],
    ctaHeading: 'See what your site could be without the template',
    ctaBody:
      'Book a call and bring your current site. You get a straight assessment of what is working, what is costing you, and what a rebuild designed for you would look like.',
  },
  {
    slug: 'wix',
    name: 'Wix',
    seoTitle: 'Wix Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and Wix for music festivals, ensembles, schools, and churches. A fast, professionally maintained website designed for your organization instead of a drag-and-drop builder you manage yourself.',
    heroHeadline: 'Your festival deserves better than a website builder',
    heroBody:
      'Wix makes it possible for anyone to publish a website. It does not make the website good, fast, or right for a music organization with a season to sell and applicants to convert. Those are different problems.',
    shortVersion:
      'Wix is a drag-and-drop builder: you pick a template, arrange blocks, and bolt on apps from its marketplace as needs come up. It works until the site matters. Slow pages, a design that reads as template, and an app stack nobody remembers configuring are what music organizations usually bring us when they leave Wix. Fugue Lab replaces that with a fast, prerendered site designed around your programs, maintained by the person who built it.',
    rows: [
      {
        dimension: 'How it gets built',
        fugueLab: 'Designed and coded for your organization by a musician who understands the domain.',
        them: 'You assemble it from template blocks and marketplace apps.',
      },
      {
        dimension: 'Speed',
        fugueLab: 'Prerendered static pages served from a CDN. Fast on a phone on festival-lawn wifi.',
        them: 'Builder output carries the editor runtime with it. Heavier pages are the norm.',
      },
      {
        dimension: 'Template lock-in',
        fugueLab: 'No template. The design is yours and evolves with you.',
        them: 'Changing templates generally means rebuilding the site.',
      },
      {
        dimension: 'Features you need',
        fugueLab:
          'Application funnels, event calendars, email capture, and payments built in as part of the engagement.',
        them: 'Marketplace apps with separate settings, and often separate fees.',
      },
      {
        dimension: 'Maintenance',
        fugueLab: 'Ours. Content edits are yours through the CMS.',
        them: 'Yours.',
      },
      {
        dimension: 'SEO',
        fugueLab: 'Real HTML per route, structured data, and redirects handled at launch.',
        them: 'Workable, but dependent on how the site was assembled.',
      },
    ],
    whereTheyFit: {
      heading: 'Where Wix fits',
      body: 'For a personal page or a brand-new group that needs any web presence this week at minimal cost, Wix does that job. The organizations that come to us from Wix are past that stage: they have programs to fill and an audience to grow, and the site has become the bottleneck instead of the tool.',
    },
    switchSteps: [
      {
        title: 'We inventory the Wix site',
        description:
          'Pages, embedded apps, forms, and where your data actually lives, so nothing gets lost in the move.',
      },
      {
        title: 'We build the replacement',
        description:
          'A new design, your content restructured around programs and season, application and contact flows rebuilt natively.',
      },
      {
        title: 'Domain and search move with you',
        description: 'DNS cutover, redirects for every old URL, and verification that rankings carry over.',
      },
    ],
    faqs: [
      {
        question: 'We have years of content on Wix. Does it all move?',
        answer:
          'Yes. Content migration is part of the build. Pages, images, blog posts, and event history come across, usually better organized than they were.',
      },
      {
        question: 'Our box office and forms run through Wix apps. What happens to those?',
        answer:
          'Each one gets inventoried and either rebuilt natively (forms, applications, email capture) or reconnected (external box office links). You see the plan before anything moves.',
      },
      {
        question: 'Is this overkill for a small ensemble?',
        answer:
          'Sometimes, honestly. If a builder page is serving you fine and nothing is riding on the site, keep it. When concerts, applications, or donations depend on the site working well, that is when the upgrade pays for itself.',
      },
    ],
    ctaHeading: 'Replace the builder with a site that carries its weight',
    ctaBody:
      'Book a call, share your Wix site, and get a concrete migration plan with timeline and cost. No rebuild starts until you have seen exactly what changes.',
  },
  {
    slug: 'wordpress',
    name: 'WordPress',
    seoTitle: 'WordPress Alternative for Music Organizations',
    seoDescription:
      'Compare Fugue Lab and WordPress for music festivals, ensembles, schools, and churches. A prerendered site with nothing to patch instead of a plugin stack that needs constant maintenance.',
    heroHeadline: 'Everything WordPress does for a music organization, without the maintenance treadmill',
    heroBody:
      'WordPress can do anything, which is exactly the problem. Somebody has to choose the theme, configure the page builder, vet the plugins, apply the security updates, and fix it when an update breaks the season page the week before opening night.',
    shortVersion:
      'WordPress powers a huge share of the web, and it earns that with flexibility. For a music organization, that flexibility usually arrives as a theme, a page builder, a dozen plugins, hosting, and a standing maintenance bill to keep the whole stack patched and compatible. Fugue Lab takes a different approach: your site is prerendered to static pages, so there is no plugin stack to patch and nothing for an attacker to log into. You edit content through a CMS; we handle everything else.',
    rows: [
      {
        dimension: 'Architecture',
        fugueLab: 'Prerendered static pages on a CDN. No server, no admin login surface, nothing to patch.',
        them: 'PHP application plus theme plus plugins, all needing updates and compatibility care.',
      },
      {
        dimension: 'Security',
        fugueLab: 'Static output. The common WordPress attack surface simply is not there.',
        them: 'A perpetual patching discipline. Outdated plugins are the classic way music-org sites get hacked.',
      },
      {
        dimension: 'Maintenance cost',
        fugueLab: 'Included in the engagement. One relationship, one bill.',
        them: 'Hosting, plugin licenses, and typically a developer or agency retainer to keep it healthy.',
      },
      {
        dimension: 'Editing experience',
        fugueLab: 'A CMS scoped to your content types: events, programs, people, news.',
        them: 'wp-admin, with the page builder your last developer happened to choose.',
      },
      {
        dimension: 'Speed',
        fugueLab: 'Static HTML, optimized images, no runtime database calls.',
        them: 'Ranges from decent to painful depending on theme, plugins, and hosting.',
      },
      {
        dimension: 'Applications and payments',
        fugueLab:
          'Native application funnels with integrated payments (Stripe, PayPal, or your preferred system) and review workflow.',
        them: 'Form plugins plus payment add-ons, assembled and maintained by you.',
      },
    ],
    whereTheyFit: {
      heading: 'Where WordPress fits',
      body: 'With a skilled developer permanently attached, WordPress remains a capable platform, and organizations that have that person on staff are usually well served. The pattern we see instead: the developer who set it up moved on years ago, updates are behind, and nobody currently at the organization knows which plugin does what. If that sounds familiar, the platform is not the problem you need to solve. The dependency is.',
    },
    switchSteps: [
      {
        title: 'We take a full export',
        description:
          'Content, media, and URL inventory out of WordPress before anything is touched. Your history is safe from step one.',
      },
      {
        title: 'We rebuild without the stack',
        description:
          'A new design, content restructured for your programs, forms and payments rebuilt natively. No theme, no plugins.',
      },
      {
        title: 'Cut over and decommission',
        description:
          'Redirects preserve every ranking URL, the old hosting gets retired, and the maintenance bill retires with it.',
      },
    ],
    faqs: [
      {
        question: 'We have hundreds of posts and pages. Can they all come over?',
        answer:
          'Yes. WordPress exports cleanly, and migrating the archive, with redirects for every URL, is a standard part of the build.',
      },
      {
        question: 'Our current site was hacked or is behind on updates. Where do we start?',
        answer:
          'Start with the call. We take the content export early so nothing more can be lost, and if the site is actively compromised we prioritize getting a clean replacement live.',
      },
      {
        question: 'What do we stop paying for after the switch?',
        answer:
          'Typically hosting, premium plugin renewals, and the maintenance retainer. What replaces them is one flat engagement with us, listed on the pricing page.',
      },
    ],
    ctaHeading: 'Retire the maintenance treadmill',
    ctaBody:
      'Book a call and bring your WordPress site, whatever state it is in. You get an honest read on what a rebuild involves and what stops being your problem afterward.',
  },
]

export function getCompetitor(slug: string): Competitor {
  const competitor = competitors.find((c) => c.slug === slug)
  if (!competitor) throw new Error(`Unknown competitor slug: ${slug}`)
  return competitor
}
